import { CoreGoogleDeviceOrganizationalUnitSettings, GoogleDeviceOUOperator, CoreOrganizationalUnitPath } from '../../../../../../models/CoreGoogleDeviceExportModels';
import { Button, FormGroup, Table, Input, FormFeedback } from 'reactstrap';
import React, { useState } from 'react';
import SelectInput from '../../../../../misc/SelectInput';
import * as DeviceTypes from './GoogleDeviceTypes';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
interface GoogleDeviceOUProps {
    organizationalUnitSettings: CoreGoogleDeviceOrganizationalUnitSettings
    disabled?: boolean
    onChange: (x: CoreGoogleDeviceOrganizationalUnitSettings) => void
}
const defaultOUPath: CoreOrganizationalUnitPath = {
    operator: GoogleDeviceOUOperator.StartsWith,
    path: ''
};
const GoogleDeviceOU: React.FC< GoogleDeviceOUProps> = (props) => {
    const [newIgnoredPath, setNewIgnoredPath] = useState<CoreOrganizationalUnitPath>(defaultOUPath);
    const [newDeniedPath, setNewDeniedPath] = useState<CoreOrganizationalUnitPath>(defaultOUPath);
    const [newIgnoredPathInvalid, setNewIgnoredPathInvalid] = useState<boolean>(false);
    const [newIgnoredOperatorInvalid, setNewIgnoredOperatorInvalid] = useState<boolean>(false);
    const [newDeniedPathInvalid, setNewDeniedPathInvalid] = useState<boolean>(false);
    const [newDeniedOperatorInvalid, setNewDeniedOperatorInvalid] = useState<boolean>(false);
    const { t } = useTranslation(['configuration', 'common'])

    const updateConfig = (change: Partial<CoreGoogleDeviceOrganizationalUnitSettings>) => {
        props.onChange({ ...props.organizationalUnitSettings, ...change })
    }

    const ignoredOUPaths = props.organizationalUnitSettings.ignoredOUPath ?? [];
    const deniedOUPaths = props.organizationalUnitSettings.deniedOUPath ?? [];

    const addIgnoredPath = () => {
        const trimmedPath = newIgnoredPath.path.trim();

        const operatorInvalid =
            newIgnoredPath.operator === undefined ||
            newIgnoredPath.operator === GoogleDeviceOUOperator.None;

        const duplicatePath = ignoredOUPaths.some(x =>
            x.operator === newIgnoredPath.operator &&
            x.path.toLowerCase() === trimmedPath.toLowerCase()
        );

        const pathInvalid = !trimmedPath || duplicatePath;

        setNewIgnoredOperatorInvalid(operatorInvalid);
        setNewIgnoredPathInvalid(pathInvalid);

        if (operatorInvalid || pathInvalid) {
            return;
        }

        updateConfig({
            ignoredOUPath: [...ignoredOUPaths, { ...newIgnoredPath, path: trimmedPath }]
        });

        setNewIgnoredPath(defaultOUPath);
        setNewIgnoredPathInvalid(false);
        setNewIgnoredOperatorInvalid(false);

    };
    const addDeniedPath = () => {
        const trimmedPath = newDeniedPath.path.trim();

        const operatorInvalid =
            newDeniedPath.operator === undefined ||
            newDeniedPath.operator === GoogleDeviceOUOperator.None;

        const duplicatePath = deniedOUPaths.some(x =>
            x.operator === newDeniedPath.operator &&
            x.path.toLowerCase() === trimmedPath.toLowerCase()
        );

        const pathInvalid = !trimmedPath || duplicatePath;

        setNewDeniedOperatorInvalid(operatorInvalid);
        setNewDeniedPathInvalid(pathInvalid);

        if (operatorInvalid || pathInvalid) {
            return;
        }

        updateConfig({
            deniedOUPath: [...deniedOUPaths, { ...newDeniedPath, path: trimmedPath }]
        });

        setNewDeniedPath(defaultOUPath);
        setNewDeniedPathInvalid(false);
        setNewDeniedOperatorInvalid(false);
    };

    const removeIgnoredPath = (index: number) => {
        updateConfig({
            ignoredOUPath: ignoredOUPaths.filter((_, i) => i !== index)
        });
    };

    const removeDeniedPath = (index: number) => {
        updateConfig({
            deniedOUPath: deniedOUPaths.filter((_, i) => i !== index)
        });
    };

    return (
        <div>
            <FormGroup className="mb-3">
                <h5> {t('googleDeviceExport.ouSettings.OUPath.ignoredTitle', { ns: 'configuration' })}</h5>
                <Table striped bordered>
                    <thead>
                        <tr>
                            <th>{t('googleDeviceExport.ouSettings.OUPath.operator', { ns: 'configuration' })}</th>
                            <th> {t('googleDeviceExport.ouSettings.OUPath.Path', { ns: 'configuration' })}</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {props.organizationalUnitSettings.ignoredOUPath.map((item, index) => (
                            <tr key={`${item.operator}-${item.path}-${index}`}>
                                <td>{t(`googleDeviceExport.ouSettings.OUPath.sourceOptions.${item.operator}`, { ns: 'configuration' })}</td>
                                <td> {item.path}</td>
                                <td>  <Button
                                    color="danger"
                                    size="sm"
                                    disabled={props.disabled}
                                    onClick={() => removeIgnoredPath(index)}
                                >
                                    <span title={t('action.remove', { ns: 'common' })}><FontAwesomeIcon icon="trash" ></FontAwesomeIcon> </span>
                                </Button></td>
                            </tr>
                        ))}
                        <tr>
                            <td >
                                <SelectInput
                                    defaultText={t('googleDeviceExport.ouSettings.OUPath.operator', { ns: 'configuration' })}
                                    name='organizationalUnitSettings.ignoredOUPath'
                                    selected={newIgnoredPath.operator}
                                    disableDefaultOption={true}
                                    hideDefaultOptionIfNotSet={true}
                                    items={DeviceTypes.DeviceOUOperator.map(item => ({
                                        value: item,
                                        text: t(`googleDeviceExport.ouSettings.OUPath.sourceOptions.${item}`, { ns: 'configuration' })
                                    }))}
                                    onChange={(_, value) => {
                                        setNewIgnoredPath({
                                            ...newIgnoredPath,
                                            operator: Number(value)
                                        });
                                    }}
                                    disabled={props.disabled}
                                    invalid={newIgnoredOperatorInvalid}
                                    invalidFeedback={t('googleDeviceExport.ouSettings.OUPath.invalidFormFeedbackOperator', { ns: 'configuration' })}
                                />
                            </td>
                            <td >
                                <Input
                                    type="text"
                                    value={newIgnoredPath.path}
                                    placeholder="/F-3/2"
                                    disabled={props.disabled}
                                    invalid={newIgnoredPathInvalid}
                                    onChange={(e) => {
                                        setNewIgnoredPath({ ...newIgnoredPath, path: e.target.value });
                                        if (newIgnoredPathInvalid) {
                                            setNewIgnoredPathInvalid(false);
                                        }
                                    }}
                                />
                                {newIgnoredPathInvalid && (
                                    <FormFeedback style={{ display: 'block' }}>
                                        {t('googleDeviceExport.ouSettings.OUPath.invalidFormFeedbackPath', { ns: 'configuration' })}
                                    </FormFeedback>
                                )}
                            </td>
                            <td>  <Button color="primary" onClick={addIgnoredPath} disabled={props.disabled || newIgnoredPath.operator === undefined || newIgnoredPath.operator === GoogleDeviceOUOperator.None}>
                                {t('action.add', { ns: 'common' })}
                            </Button></td>
                        </tr>

                    </tbody>

                </Table>

            </FormGroup>

            <FormGroup className="mb-3">
                <h5> {t('googleDeviceExport.ouSettings.OUPath.deniedTitle', { ns: 'configuration' })}</h5>
                <Table striped bordered>
                    <thead>
                        <tr>
                            <th>{t('googleDeviceExport.ouSettings.OUPath.operator', { ns: 'configuration' })}</th>
                            <th> {t('googleDeviceExport.ouSettings.OUPath.Path', { ns: 'configuration' })}</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {props.organizationalUnitSettings.deniedOUPath.map((item, index) => (
                            <tr key={`${item.operator}-${item.path}-${index}`}>
                                <td>{t(`googleDeviceExport.ouSettings.OUPath.sourceOptions.${item.operator}`, { ns: 'configuration' })}</td>
                                <td> {item.path}</td>
                                <td>  <Button
                                    color="danger"
                                    size="sm"
                                    disabled={props.disabled}
                                    onClick={() => removeDeniedPath(index)}
                                >
                                    <span title={t('action.remove', { ns: 'common' })}><FontAwesomeIcon icon="trash" ></FontAwesomeIcon> </span>
                                    </Button>
                                </td>
                            </tr>
                        ))}
                        <tr>
                            <td>
                                <SelectInput
                                    defaultText={t('googleDeviceExport.ouSettings.OUPath.operator', { ns: 'configuration' })}
                                    name='organizationalUnitSettings.deniedOUPath'
                                    selected={newDeniedPath.operator}
                                    disableDefaultOption={true}
                                    hideDefaultOptionIfNotSet={true}
                                    items={DeviceTypes.DeviceOUOperator.map(item => ({
                                        value: item,
                                        text: t(`googleDeviceExport.ouSettings.OUPath.sourceOptions.${item}`, { ns: 'configuration' })
                                    }))}
                                    onChange={(_, value) => {
                                        setNewDeniedPath({
                                            ...newDeniedPath,
                                            operator: Number(value)
                                        });
                                    }}
                                    disabled={props.disabled}
                                    invalid={newDeniedOperatorInvalid}
                                    invalidFeedback={t('googleDeviceExport.ouSettings.OUPath.invalidFormFeedbackOperator', { ns: 'configuration' })}
                                />
                            </td>
                            <td >
                                <Input
                                    type="text"
                                    value={newDeniedPath.path}
                                    placeholder="/F-3/2"
                                    disabled={props.disabled}
                                    invalid={newDeniedPathInvalid}
                                    onChange={(e) => {
                                        setNewDeniedPath({ ...newDeniedPath, path: e.target.value });
                                        if (newDeniedPathInvalid) {
                                            setNewDeniedPathInvalid(false);
                                        }
                                    }}
                                />
                                {newDeniedPathInvalid && (
                                    <FormFeedback style={{ display: 'block' }}>
                                        {t('googleDeviceExport.ouSettings.OUPath.invalidFormFeedbackPath', { ns: 'configuration' })}
                                    </FormFeedback>
                                )}
                            </td>
                            <td>  <Button color="primary" onClick={addDeniedPath} disabled={props.disabled || newDeniedPath.operator === undefined || newDeniedPath.operator === GoogleDeviceOUOperator.None}>
                                {t('action.add', { ns: 'common' })}
                            </Button></td>
                        </tr>
                    </tbody>
                </Table>

            </FormGroup>
        </div>
    ); 
}
export default GoogleDeviceOU;


